import {
  isModelVirtualWeatherStation,
  isVirtualWeatherStation
} from '@weenat/client/dist/core/devices'
import {
  fromWindAngleToDirection,
  MetricSummaryValue as MetricSummaryValueInterface,
  useMetricSummary
} from '@weenat/client/dist/core/metrics'
import { isDevice, isPlot, isStation } from '@weenat/client/dist/core/utils/resourcesTypeCheckers'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import { Device, DeviceAvailableMeasure } from '@weenat/client/dist/resources/devices'
import { ForecastResults } from '@weenat/client/dist/resources/forecasts'
import { SummaryData } from '@weenat/client/dist/resources/measurements.type'
import { PlotAvailableMeasure } from '@weenat/client/dist/resources/plots'
import { Station } from '@weenat/client/dist/resources/stations'
import { useIntl } from '@weenat/wintl'
import { useIsAppAccessRestricted } from 'app/src/hooks/useIsAppAccessRestricted'
import Compass from 'app/src/kit/Compass'
import Icons from 'app/src/kit/Icons'
import SmartText from 'app/src/kit/SmartText'
import Text from 'app/src/kit/Text'
import TextNumber from 'app/src/kit/TextNumber'
import LeafIntensity from 'app/src/metrics/LeafIntensity'
import { useToggleFeature } from 'app/state'
import { isNil } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { useTheme } from 'styled-components'
import MetricSummaryValue from './MetricSummaryValue'

interface DumbMetricSummaryCondensedProps {
  /** Timestamp at which the latest data was received */
  metric: DeviceAvailableMeasure | PlotAvailableMeasure
  forecastsResults: ForecastResults | undefined
  resource: Device | Station
  summary: SummaryData
  isExpertOrg: boolean
}

const LegacyDumbMetricSummaryCondensed: FC<DumbMetricSummaryCondensedProps> = ({
  metric,
  forecastsResults,
  resource,
  summary,
  isExpertOrg
}) => {
  const { colors, typography } = useTheme()
  const { reason } = useIsAppAccessRestricted({
    isVWS: isPlot(resource)
      ? undefined
      : (isDevice(resource) && isVirtualWeatherStation(resource)) ||
        (isStation(resource) && isModelVirtualWeatherStation(resource.model))
  })
  const [{ smv }] = useToggleFeature()

  const { getFullSummaryValue } = useMetricSummary({
    metric,
    forecastsResults,
    resource,
    summary,
    isExpertOrg,
    smv
  })

  const getMetricSummaries = useCallback(
    (aMetric: DeviceAvailableMeasure | PlotAvailableMeasure): MetricSummaryValueInterface[] => {
      const metricSummaries = []
      switch (aMetric) {
        case 'U':
        case 'T_SOIL':
        case 'T_CAL':
        case 'T_WET':
        case 'T_DRY':
        case 'T':
        case 'FF':
        case 'SSI':
        case 'T_DEW':
        case 'PPFD':
        case 'LW_V':
          metricSummaries.push(getFullSummaryValue('latest'))
          break
        case 'RR':
          metricSummaries.push(getFullSummaryValue('today'))
          metricSummaries.push(getFullSummaryValue('yesterday'))
          metricSummaries.push(getFullSummaryValue('week'))
          break
        default:
          break
      }
      return metricSummaries
    },
    [getFullSummaryValue]
  )

  const metricSummaries = useMemo(() => getMetricSummaries(metric), [getMetricSummaries, metric])

  const labelStyle = {
    color: colors.grayscale.black,
    fontSize: `${typography.sizes.xs}px`,
    fontWeight: typography.weights.light
  }

  return (
    <Flex $flex={1} $gap='md' $p='md' $alignItems='center' $justifyContent='space-around'>
      {metricSummaries.map((mSummary) => (
        <MetricSummaryValue
          metric={metric}
          key={`${mSummary.aggregationId}_${mSummary.label}`}
          showLabel={metricSummaries.length > 1}
          isShrunk
          summaryValue={mSummary}
          reason={reason}
          labelStyle={labelStyle}
        />
      ))}
    </Flex>
  )
}

const NewDumbMetricSummaryCondensed: FC<DumbMetricSummaryCondensedProps> = ({
  metric,
  forecastsResults,
  resource,
  summary,
  isExpertOrg
}) => {
  const { t } = useIntl()
  const { convertValue, convertDuration } = useConvertedValue()
  const { isRestricted } = useIsAppAccessRestricted()
  const [{ smv }] = useToggleFeature()

  const { getFullSummaryValue } = useMetricSummary({
    metric,
    forecastsResults,
    resource,
    summary,
    isExpertOrg,
    smv
  })

  const latest = getFullSummaryValue('latest')

  const latestValue = convertValue({
    value: latest.value,
    metric,
    showEmptyState: true
  })

  switch (metric) {
    case 'U':
    case 'T_SOIL':
    case 'T_CAL':
    case 'T_WET':
    case 'T_DRY':
    case 'T':
    case 'SSI':
    case 'T_DEW':
    case 'PPFD':
      return (
        <Flex $px='md' $pt={'sm'} $pb={'md'} $gap='md'>
          <Box $minWidth={0} $flex={1}>
            {isRestricted ? (
              <Icons.Lock $size='lg' />
            ) : (
              <TextNumber $fontSize={'lg'} $lineHeight='lg' $fontWeight='bold'>
                {latestValue}
              </TextNumber>
            )}
            <Text
              $fontSize='sm'
              $lineHeight='sm'
              $color={isRestricted ? 'grayscale.500' : undefined}
              $ellipsis
            >
              {t('models.device.last_measurement', { capitalize: true })}
            </Text>
          </Box>
        </Flex>
      )

    case 'RR':
      const today = getFullSummaryValue('today')
      const yesterday = getFullSummaryValue('yesterday')
      const week = getFullSummaryValue('week')

      const todayValue = convertValue({
        value: today.value,
        metric,
        showEmptyState: true
      })

      const yesterdayValue = convertValue({
        value: yesterday.value,
        metric,
        showEmptyState: true
      })

      const weekValue = convertValue({
        value: week.value,
        metric,
        showEmptyState: true
      })

      return (
        <Flex $px='md' $pt={'sm'} $pb={'md'} $gap='md'>
          <Flex $flexDirection='column' $justifyContent='flex-end' $minWidth={0} $flex={1}>
            {isRestricted ? (
              <Icons.Lock $size='lg' />
            ) : (
              <TextNumber $fontSize={'lg'} $lineHeight='lg' $fontWeight='bold'>
                {todayValue}
              </TextNumber>
            )}
            <Text
              $fontSize='sm'
              $lineHeight='sm'
              $color={isRestricted ? 'grayscale.500' : undefined}
              $ellipsis
            >
              {today.label}
            </Text>
          </Flex>
          <Flex
            $flexDirection='column'
            $justifyContent='flex-end'
            height={'100%'}
            $minWidth={0}
            $flex={0.5}
          >
            {isRestricted ? (
              <Icons.Lock $size='lg' />
            ) : (
              <TextNumber $fontSize={'rg'} $lineHeight='lg' $fontWeight='bold'>
                {yesterdayValue}
              </TextNumber>
            )}
            <Text
              $fontSize='sm'
              $lineHeight='sm'
              $color={isRestricted ? 'grayscale.500' : undefined}
              $ellipsis
            >
              {yesterday.label}
            </Text>
          </Flex>
          <Flex $flexDirection='column' $justifyContent='flex-end' $minWidth={0} $flex={0.5}>
            {isRestricted ? (
              <Icons.Lock $size='lg' />
            ) : (
              <TextNumber $fontSize={'rg'} $lineHeight='lg' $fontWeight='bold'>
                {weekValue}
              </TextNumber>
            )}
            <Text
              $fontSize='sm'
              $lineHeight='sm'
              $color={isRestricted ? 'grayscale.500' : undefined}
              $ellipsis
            >
              {week.label}
            </Text>
          </Flex>
        </Flex>
      )

    case 'LW_V':
      const rawDuration = getFullSummaryValue('LW_D', 'lastTwelveHours')
      const duration = !isNil(rawDuration.value)
        ? convertDuration({ value: rawDuration.value })
        : null

      const intensity = !isNil(latest.value) && !isNil(latestValue) ? latestValue : null

      const currentFlag = !isNil(intensity)
        ? `${t(`dss.leafWetness.flags.${parseInt(intensity, 10) < 1 ? 'dry' : 'wet'}`, {
            capitalize: true
          })} (${latestValue.trim()} / 10)`
        : undefined

      return (
        <Flex $flexDirection='column' $px='md' $pt={'sm'} $pb={'md'} $gap='md'>
          <Box $minWidth={0}>
            {isRestricted ? (
              <Icons.Lock $size='lg' />
            ) : (
              <>
                <Flex $alignItems='center' $justifyContent='space-between' $p={'md'}>
                  <Box>
                    <TextNumber $fontSize={'lg'} $lineHeight='lg' $fontWeight='bold'>
                      {currentFlag}
                    </TextNumber>
                    <Text
                      $fontSize='sm'
                      $lineHeight='sm'
                      $color={isRestricted ? 'grayscale.500' : undefined}
                      $ellipsis
                    >
                      {t('models.device.last_measurement', { capitalize: true })}
                    </Text>
                  </Box>
                  {!isNil(intensity) ? <LeafIntensity intensity={parseInt(intensity, 10)} /> : null}
                </Flex>
                <Box $borderRadius={'md'} $backgroundColor={'alpha.black.10'} $p={'md'}>
                  {rawDuration.value === 0 ? (
                    <SmartText tkey='dashboards.no_leaf_wetness' $fontSize='lg' />
                  ) : (
                    <SmartText
                      tkey='dashboards.leaf_wetness_duration'
                      vars={{
                        duration
                      }}
                      $fontSize='lg'
                    />
                  )}

                  <Text
                    $fontSize='sm'
                    $lineHeight='sm'
                    $color={isRestricted ? 'grayscale.500' : undefined}
                    $ellipsis
                  >
                    {t('dashboards.leaf_wetness_time_range')}
                  </Text>
                </Box>
              </>
            )}
          </Box>
        </Flex>
      )

    case 'FF':
      const direction = getFullSummaryValue('DD')
      const gust = getFullSummaryValue('FXY')

      const gustValue = convertValue({
        value: gust.value,
        metric,
        showEmptyState: true
      })

      const directionValue = convertValue({
        value: direction.value,
        metric: 'DD',
        showEmptyState: true
      })

      return (
        <Flex $px='md' $pt={'sm'} $pb={'md'} $gap='md'>
          <Flex $flexDirection='column' $flex={1} $gap={'md'}>
            {/* AVERAGE */}
            <Box $minWidth={0}>
              {isRestricted ? (
                <Icons.Lock $size='lg' />
              ) : (
                <TextNumber $fontWeight='bold' $fontSize='lg' $lineHeight='sm'>
                  {latestValue}
                </TextNumber>
              )}
              <Text
                $fontSize='sm'
                $lineHeight='sm'
                $color={isRestricted ? 'grayscale.500' : undefined}
                $ellipsis
              >
                {t('map.universes.aggregate.real-time-FF', { capitalize: true })}
              </Text>
            </Box>
            {/* GUSTS */}
            <Box $minWidth={0}>
              {isRestricted ? (
                <Icons.Lock $size='lg' />
              ) : (
                <TextNumber $fontSize={'rg'} $lineHeight='lg' $fontWeight='bold'>
                  {gustValue}
                </TextNumber>
              )}
              <Text
                $fontSize='sm'
                $lineHeight='sm'
                $color={isRestricted ? 'grayscale.500' : undefined}
                $ellipsis
              >
                {t('map.universes.aggregate.real-time-FXY', { capitalize: true })}
              </Text>
            </Box>
            {/* DIRECTION */}
            <Box $minWidth={0}>
              {isRestricted ? (
                <Icons.Lock $size='lg' />
              ) : (
                <Flex $gap='sm' $flexWrap='wrap'>
                  <TextNumber $fontSize={'rg'} $lineHeight='lg' $fontWeight='bold'>
                    {directionValue}
                  </TextNumber>
                  {!isNil(direction.value) ? (
                    <Text $fontSize={'rg'} $lineHeight='lg' $fontWeight='bold'>
                      {t(`windDirections.${fromWindAngleToDirection(direction.value)}`)}
                    </Text>
                  ) : null}
                </Flex>
              )}
              <Text
                $fontSize='sm'
                $lineHeight='sm'
                $color={isRestricted ? 'grayscale.500' : undefined}
                $ellipsis
              >
                {t('map.universes.aggregate.real-time-DD', { capitalize: true })}
              </Text>
            </Box>
          </Flex>
          <Box
            $minHeight={128}
            $minWidth={128}
            $borderRadius={'md'}
            $p={'md'}
            $backgroundColor={'alpha.black.5'}
            $borderWidth={1}
            $borderColor={'alpha.black.5'}
          >
            <Compass windOrigin={direction.value} />
          </Box>
        </Flex>
      )
    default:
      return null
  }
}

const DumbMetricSummaryCondensed: FC<DumbMetricSummaryCondensedProps> = ({
  metric,
  forecastsResults,
  resource,
  summary,
  isExpertOrg
}) => {
  const [{ sdh }] = useToggleFeature()

  return sdh ? (
    <NewDumbMetricSummaryCondensed
      metric={metric}
      resource={resource}
      summary={summary}
      isExpertOrg={isExpertOrg}
      forecastsResults={forecastsResults}
    />
  ) : (
    <LegacyDumbMetricSummaryCondensed
      metric={metric}
      resource={resource}
      summary={summary}
      isExpertOrg={isExpertOrg}
      forecastsResults={forecastsResults}
    />
  )
}

export default DumbMetricSummaryCondensed
